import React, { useEffect, useState, useRef } from "react";
import { api, formatCurrency, formatApiErrorDetail, toImageSrc, compressImage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import * as SliderPrimitive from "@radix-ui/react-slider";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  TrendUp, CurrencyInr, House as HouseIcon, Sparkle, Plus, DotsThreeVertical,
  CheckCircle, Users, X, MapPin, Ruler, Bed, CaretLeft, CaretRight, Image as ImageIcon,
  Envelope, Phone, ShareNetwork, WhatsappLogo, Copy, PaperPlaneTilt, Target,
} from "@phosphor-icons/react";
import { CreateReferralDialog } from "@/pages/ReferralsPage";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid,
} from "recharts";

const HERO_BG = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2000&q=80";

const SPECIALTY_HEROES = {
  Luxury: [
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2000&q=80",   // modern luxury villa
    "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=2000&q=80",   // architectural luxury exterior
    "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=2000&q=80",   // luxury house with pool
  ],
  Residential: [
    "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=2000&q=80",   // warm residential
    "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=2000&q=80",   // suburban home
    "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=2000&q=80",   // modern residential
  ],
  Commercial: [
    "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2000&q=80",   // modern office interior
    "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2000&q=80",   // commercial skyline
    "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=2000&q=80",   // boardroom
  ],
};

/**
 * Returns a curated gold tint based on the agent's average sale price.
 *   < ₹1 Cr        → warm amber  (entry-level / new agents)
 *   ₹1 - 5 Cr      → classic gold (default — most agents)
 *   ₹5 - 15 Cr     → champagne   (premium)
 *   > ₹15 Cr       → platinum    (ultra-luxury)
 */
function priceTierAccent(avgPrice) {
  const v = Number(avgPrice) || 0;
  if (v >= 150000000) return { tier: "Platinum",  color: "#EBD89A", soft: "rgba(235,216,154,0.22)" };
  if (v >= 50000000)  return { tier: "Champagne", color: "#E2C073", soft: "rgba(226,192,115,0.22)" };
  if (v >= 10000000)  return { tier: "Gold",      color: "#D4AF37", soft: "rgba(212,175,55,0.22)" };
  return                     { tier: "Bronze",    color: "#C58A3F", soft: "rgba(197,138,63,0.22)" };
}

const SUBTYPES = {
  Commercial: ["Office", "Hotels", "Plots", "Shops"],
  Residential: ["Flats", "Bungalows", "Penthouse", "Villas"],
  Plots: [],
};

function TableSkeleton({ rows = 5, cols = 5 }) {
  return (
    <div className="card-surface rounded-xl overflow-hidden" data-testid="table-skeleton">
      <div className="px-5 py-3 border-b hairline flex gap-4">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className={`h-3 ${i === 0 ? "w-1/3" : "w-20"} bg-white/[0.06]`} />
        ))}
      </div>
      <div className="divide-y hairline">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="px-5 py-4 flex items-center gap-4">
            <Skeleton className="h-12 w-12 rounded-md bg-white/[0.08] shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-2/3 bg-white/[0.08]" />
              <Skeleton className="h-3 w-1/3 bg-white/[0.05]" />
            </div>
            {Array.from({ length: Math.max(cols - 2, 0) }).map((_, c) => (
              <Skeleton key={c} className="h-4 w-20 bg-white/[0.06] hidden md:block" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ location: "", min_price: "", max_price: "", min_size: "", status: "all" });
  const [propDialog, setPropDialog] = useState(false);
  const [clientDialog, setClientDialog] = useState(false);
  const [detailProp, setDetailProp] = useState(null);
  const [detailClient, setDetailClient] = useState(null);

  const loadAll = async () => {
    try {
      const q = {};
      if (filters.location) q.location = filters.location;
      if (filters.min_price) q.min_price = filters.min_price;
      if (filters.max_price) q.max_price = filters.max_price;
      if (filters.min_size) q.min_size = filters.min_size;
      if (filters.status && filters.status !== "all") q.status = filters.status;
      const [a, p, c] = await Promise.all([
        api.get("/analytics/me"),
        api.get("/properties", { params: q }),
        api.get("/clients"),
      ]);
      setAnalytics(a.data);
      setProperties(p.data);
      setClients(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [filters]); // eslint-disable-line

  const markSold = async (pid) => {
    try {
      await api.patch(`/properties/${pid}`, { status: "sold" });
      toast.success("Marked as sold");
      loadAll();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const remove = async (pid) => {
    if (!window.confirm("Delete this property?")) return;
    await api.delete(`/properties/${pid}`);
    loadAll();
    toast.success("Deleted");
  };

  const removeClient = async (cid) => {
    await api.delete(`/clients/${cid}`);
    loadAll();
  };

  const kpis = analytics
    ? [
        { label: "Success Rate", value: `${analytics.success_rate}%`, icon: TrendUp, hint: `${analytics.sold}/${analytics.total_properties} closed` },
        { label: "Total Sales", value: formatCurrency(analytics.total_sales_value), icon: CurrencyInr, hint: `${analytics.sold} deals` },
        { label: "Active Listings", value: analytics.available, icon: HouseIcon, hint: `Value ${formatCurrency(analytics.total_inventory_value)}` },
        { label: "Avg Price", value: formatCurrency(analytics.avg_price), icon: Sparkle, hint: `${analytics.clients} clients` },
      ]
    : [];

  const inventoryRows = properties.filter((p) =>
    filters.status === "all" ? true : p.status === filters.status
  );

  return (
    <div className="min-h-screen bg-ink-900" data-testid="dashboard-page">
      {/* Hero */}
      <HeroBackground
        specialty={user?.specialty}
        location={`${user?.area || ""}${user?.city ? `, ${user.city}` : ""}`}
        accent={priceTierAccent(analytics?.avg_price)}
      />

      <div className="p-6 md:p-10 space-y-8 pb-24">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger" data-testid="dashboard-kpis">
          {loading && kpis.length === 0
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="card-surface rounded-xl p-5" data-testid={`kpi-skeleton-${i}`}>
                  <Skeleton className="h-3 w-20 bg-white/[0.06]" />
                  <Skeleton className="h-8 w-28 mt-4 bg-white/[0.08]" />
                  <Skeleton className="h-3 w-24 mt-2 bg-white/[0.05]" />
                </div>
              ))
            : kpis.map((k) => {
                const testId = `kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`;
                return (
                  <div
                    key={k.label}
                    data-testid={testId}
                    className="card-surface rounded-xl p-5 transition-all duration-300 hover:-translate-y-[2px]"
                  >
                    <div className="flex items-start justify-between">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{k.label}</div>
                      <k.icon size={18} weight="duotone" className="text-gold" />
                    </div>
                    <div className="font-mono text-3xl mt-3 tracking-tight" data-testid={`${testId}-value`}>
                      {k.value}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{k.hint}</div>
                  </div>
                );
              })}
        </div>

        {/* Tabs (Inventory / Sold / Clients) — ABOVE graphs */}
        <Tabs defaultValue="inventory">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <TabsList className="bg-ink-700 border hairline">
              <TabsTrigger value="inventory" data-testid="tab-inventory">Inventory</TabsTrigger>
              <TabsTrigger value="sold" data-testid="tab-sold">Sold</TabsTrigger>
              <TabsTrigger value="clients" data-testid="tab-clients">Clients</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              <AddPropertyDialog open={propDialog} setOpen={setPropDialog} onSaved={loadAll} user={user} />
              <AddClientDialog open={clientDialog} setOpen={setClientDialog} onSaved={loadAll} />
            </div>
          </div>

          <TabsContent value="inventory" className="mt-5 space-y-4">
            <FiltersBar filters={filters} setFilters={setFilters} />
            <div className="text-xs text-zinc-500">{loading ? "Loading…" : `${inventoryRows.length} properties`}</div>
            {loading ? (
              <TableSkeleton rows={5} cols={5} />
            ) : (
              <PropertiesTable
                rows={inventoryRows}
                onSold={markSold}
                onDelete={remove}
                onRowClick={setDetailProp}
              />
            )}
          </TabsContent>

          <TabsContent value="sold" className="mt-5">
            {loading ? (
              <TableSkeleton rows={4} cols={5} />
            ) : (
              <PropertiesTable
                rows={properties.filter((p) => p.status === "sold")}
                soldView
                onDelete={remove}
                onRowClick={setDetailProp}
              />
            )}
          </TabsContent>

          <TabsContent value="clients" className="mt-5">
            <div className="text-xs text-zinc-500 mb-3">{loading ? "Loading…" : `${clients.length} clients`}</div>
            {loading ? (
              <TableSkeleton rows={4} cols={4} />
            ) : (
              <ClientsTable rows={clients} onDelete={removeClient} onRowClick={setDetailClient} />
            )}
          </TabsContent>
        </Tabs>

        {/* Charts BELOW the tabs per request */}
        {analytics && (
          <div className="grid md:grid-cols-3 gap-4">
            <div className="card-surface rounded-xl p-5 md:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.25em] text-zinc-400">Monthly Closings</div>
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics.monthly_trend}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="month" stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ backgroundColor: "#121216", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                    <Line type="monotone" dataKey="count" stroke="#D4AF37" strokeWidth={2} dot={{ fill: "#D4AF37", r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="card-surface rounded-xl p-5">
              <div className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">Inventory Mix</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.type_breakdown}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="type" stroke="#71717A" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ backgroundColor: "#121216", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                    <Bar dataKey="count" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Property detail modal */}
      <PropertyDetailDialog
        prop={detailProp}
        onClose={() => setDetailProp(null)}
        onSaved={() => { setDetailProp(null); loadAll(); }}
      />
      <ClientDetailDialog
        client={detailClient}
        onClose={() => setDetailClient(null)}
        onSaved={() => { setDetailClient(null); loadAll(); }}
      />
    </div>
  );
}

function HeroBackground({ specialty, location, accent }) {
  const list = SPECIALTY_HEROES[specialty] || SPECIALTY_HEROES.Luxury;
  const [idx, setIdx] = useState(0);
  const bgRef = useRef(null);
  const rafRef = useRef(0);

  // Cycle images every ~7s with a crossfade
  useEffect(() => {
    setIdx(0);
    if (list.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), 7000);
    return () => clearInterval(t);
  }, [list.length, specialty]);

  // Parallax: translate the bg layer based on scroll
  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        if (!bgRef.current) return;
        const offset = Math.min(window.scrollY, 600) * 0.35;
        bgRef.current.style.transform = `translate3d(0, ${offset}px, 0) scale(1.08)`;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const accentColor = accent?.color || "#D4AF37";
  const accentSoft = accent?.soft || "rgba(212,175,55,0.22)";

  return (
    <div
      className="relative h-64 md:h-80 border-b hairline overflow-hidden"
      data-testid="dashboard-hero"
      data-hero-tier={accent?.tier || "Gold"}
    >
      {/* Crossfading background layers */}
      <div ref={bgRef} className="absolute inset-0 will-change-transform">
        {list.map((src, i) => (
          <div
            key={src}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-[1500ms] ease-out"
            style={{
              backgroundImage: `url(${src})`,
              opacity: i === idx ? 1 : 0,
            }}
            data-testid={`hero-image-${i}`}
            data-active={i === idx ? "true" : "false"}
          />
        ))}
      </div>

      {/* Cinematic dark gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-ink-900/95 via-ink-900/75 to-ink-900/50 pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink-900 to-transparent pointer-events-none" />

      {/* Accent radial spotlight (color shifts with price tier) */}
      <div
        className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full pointer-events-none transition-colors duration-700"
        style={{ background: `radial-gradient(circle, ${accentSoft} 0%, transparent 60%)` }}
      />

      {/* Subtle grid pattern using the accent color */}
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(${accentColor} 1px, transparent 1px), linear-gradient(90deg, ${accentColor} 1px, transparent 1px)`,
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse at top right, black 0%, transparent 70%)",
          WebkitMaskImage: "radial-gradient(ellipse at top right, black 0%, transparent 70%)",
        }}
      />

      {/* Hairline corner accents in tier color */}
      <div className="absolute top-6 right-6 w-16 h-16 pointer-events-none">
        <div
          className="absolute top-0 right-0 w-full h-px"
          style={{ background: `linear-gradient(to left, ${accentColor}, transparent)` }}
        />
        <div
          className="absolute top-0 right-0 h-full w-px"
          style={{ background: `linear-gradient(to bottom, ${accentColor}, transparent)` }}
        />
      </div>
      <div className="absolute bottom-6 left-6 w-16 h-16 pointer-events-none hidden lg:block">
        <div
          className="absolute bottom-0 left-0 w-full h-px"
          style={{ background: `linear-gradient(to right, ${accentColor}, transparent)` }}
        />
        <div
          className="absolute bottom-0 left-0 h-full w-px"
          style={{ background: `linear-gradient(to top, ${accentColor}, transparent)` }}
        />
      </div>

      {/* Content */}
      <div className="relative h-full flex flex-col justify-end p-8 md:p-12 animate-fade-up">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-px w-10" style={{ background: accentColor }} />
          <div
            className="text-[10px] uppercase tracking-[0.4em] font-medium"
            style={{ color: accentColor }}
            data-testid="hero-tier-label"
          >
            Command Center · {accent?.tier || "Gold"} Tier
          </div>
        </div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.05] max-w-3xl">
          Your inventory{" "}
          <span className="italic transition-colors duration-700" style={{ color: accentColor }}>
            at a glance.
          </span>
        </h1>
        <div className="text-sm text-zinc-300/80 mt-3 flex items-center gap-2 font-light">
          <MapPin size={12} style={{ color: accentColor }} />
          <span className="tracking-wide">{location || "—"}</span>
        </div>

        {/* Slide indicators */}
        {list.length > 1 && (
          <div className="absolute bottom-4 right-8 md:right-12 flex gap-1.5">
            {list.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className="h-1 rounded-full transition-all duration-500"
                style={{
                  width: i === idx ? 24 : 8,
                  background: i === idx ? accentColor : "rgba(255,255,255,0.25)",
                }}
                data-testid={`hero-dot-${i}`}
                aria-label={`Show hero image ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FiltersBar({ filters, setFilters }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  // Snap price slider to common Indian rupee buckets.
  // 0 → 50 L → 1 Cr → 2 Cr → 5 Cr → 10 Cr → 25 Cr → 50 Cr (sale scale)
  const PRICE_STOPS = [0, 5_000_000, 10_000_000, 20_000_000, 50_000_000, 100_000_000, 250_000_000, 500_000_000];
  const MAX_IDX = PRICE_STOPS.length - 1;
  const valueToIdx = (v) => {
    if (!v) return null;
    const num = Number(v);
    let best = 0; let bestDiff = Infinity;
    PRICE_STOPS.forEach((s, i) => {
      const d = Math.abs(s - num);
      if (d < bestDiff) { bestDiff = d; best = i; }
    });
    return best;
  };
  const minIdx = filters.min_price ? valueToIdx(filters.min_price) : 0;
  const maxIdx = filters.max_price ? valueToIdx(filters.max_price) : MAX_IDX;

  const onSliderChange = (vals) => {
    const [lo, hi] = vals;
    setFilters((f) => ({
      ...f,
      min_price: lo === 0 ? "" : String(PRICE_STOPS[lo]),
      max_price: hi === MAX_IDX ? "" : String(PRICE_STOPS[hi]),
    }));
  };

  return (
    <div className="card-surface rounded-xl p-4 space-y-4" data-testid="inventory-filters">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Input placeholder="Location (e.g. Bandra, Mumbai)" value={filters.location} onChange={(e) => set("location", e.target.value)} data-testid="filter-location" className="bg-ink-700 border-white/10" />
        <Input placeholder="Min sqft" type="number" value={filters.min_size} onChange={(e) => set("min_size", e.target.value)} data-testid="filter-min-size" className="bg-ink-700 border-white/10 font-mono" />
        <Select value={filters.status} onValueChange={(v) => set("status", v)}>
          <SelectTrigger className="bg-ink-700 border-white/10" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="sold">Sold</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Price range slider (Indian rupee stops) */}
      <div className="px-1" data-testid="filter-price-slider">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Price range</div>
          <div className="text-xs text-gold font-mono" data-testid="filter-price-range-label">
            {formatCurrency(PRICE_STOPS[minIdx])} — {maxIdx === MAX_IDX ? "₹50 Cr+" : formatCurrency(PRICE_STOPS[maxIdx])}
          </div>
        </div>
        <SliderPrimitive.Root
          className="relative flex w-full touch-none select-none items-center h-5"
          min={0}
          max={MAX_IDX}
          step={1}
          value={[minIdx, maxIdx]}
          onValueChange={onSliderChange}
          minStepsBetweenThumbs={1}
        >
          <SliderPrimitive.Track className="relative h-1 w-full grow overflow-hidden rounded-full bg-white/10">
            <SliderPrimitive.Range className="absolute h-full bg-gold" />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb
            data-testid="filter-price-thumb-min"
            className="block h-4 w-4 rounded-full bg-gold border border-gold shadow-[0_0_0_3px_rgba(212,175,55,0.15)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40 transition-shadow"
            aria-label="Minimum price"
          />
          <SliderPrimitive.Thumb
            data-testid="filter-price-thumb-max"
            className="block h-4 w-4 rounded-full bg-gold border border-gold shadow-[0_0_0_3px_rgba(212,175,55,0.15)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40 transition-shadow"
            aria-label="Maximum price"
          />
        </SliderPrimitive.Root>
        <div className="flex justify-between mt-2 text-[10px] text-zinc-600 font-mono">
          <span>₹0</span><span>₹50 L</span><span>₹1 Cr</span><span>₹2 Cr</span><span>₹5 Cr</span><span>₹10 Cr</span><span>₹25 Cr</span><span>₹50 Cr+</span>
        </div>
      </div>
    </div>
  );
}

function PropertiesTable({ rows, onSold, onDelete, soldView, onRowClick }) {
  if (!rows?.length)
    return (
      <div className="card-surface rounded-xl p-10 text-center text-zinc-500" data-testid="no-properties">
        No properties in this tab. Add one to get started.
      </div>
    );
  return (
    <div className="card-surface rounded-xl overflow-hidden" data-testid="properties-table">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-b hairline">
              <TableHead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Property</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Location</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Type</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Size</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{soldView ? "Sold Price" : "Price"}</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Status</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow
                key={p.id}
                className="hover:bg-white/[0.04] border-b hairline cursor-pointer"
                data-testid={`property-row-${p.id}`}
                onClick={(e) => {
                  // Don't open detail if clicking the action menu
                  if (e.target.closest("[data-row-action]")) return;
                  onRowClick?.(p);
                }}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-14 rounded bg-ink-700 bg-cover bg-center" style={{ backgroundImage: p.image_url ? `url(${toImageSrc(p.image_url)})` : undefined }} />
                    <div>
                      <div className="text-sm">{p.title}</div>
                      <div className="text-xs text-zinc-500">
                        {p.property_type}{p.sub_type ? ` · ${p.sub_type}` : ""}{p.bedrooms ? ` · ${p.bedrooms} BHK` : ""}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-zinc-300">{p.area || p.location}{p.city ? `, ${p.city}` : ""}</TableCell>
                <TableCell className="text-sm text-zinc-400">{p.sub_type || p.property_type}</TableCell>
                <TableCell className="font-mono text-sm">{p.size_sqft} sqft</TableCell>
                <TableCell className="font-mono text-sm text-gold">
                  {formatCurrency(soldView ? p.sold_price || p.price : p.price)}
                  {p.listing_for === "rent" ? <span className="text-zinc-500 text-[10px] font-sans ml-1">/mo</span> : null}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-1">
                    <Badge
                      className={
                        p.status === "sold"
                          ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/10"
                          : "bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/5"
                      }
                    >
                      {p.status}
                    </Badge>
                    {p.listing_for === "rent" && (
                      <Badge className="bg-sky-500/10 text-sky-300 border border-sky-500/20 hover:bg-sky-500/10 text-[9px]">For Rent</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right" data-row-action>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1.5 rounded hover:bg-white/5 text-zinc-400" data-testid={`property-menu-${p.id}`}>
                        <DotsThreeVertical size={16} weight="bold" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-ink-700 border-white/10">
                      {p.status !== "sold" && onSold && (
                        <DropdownMenuItem onClick={() => onSold(p.id)} data-testid={`mark-sold-${p.id}`}>
                          <CheckCircle size={14} className="mr-2" /> Mark sold
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => onDelete(p.id)} className="text-red-400" data-testid={`delete-property-${p.id}`}>
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ClientsTable({ rows, onDelete, onRowClick }) {
  if (!rows?.length) return (
    <div className="card-surface rounded-xl p-10 text-center text-zinc-500" data-testid="no-clients">
      No clients yet.
    </div>
  );
  return (
    <div className="card-surface rounded-xl overflow-hidden" data-testid="clients-table">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-b hairline">
            <TableHead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Name</TableHead>
            <TableHead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Requirement</TableHead>
            <TableHead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Budget</TableHead>
            <TableHead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Contact</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((c) => (
            <TableRow
              key={c.id}
              className="hover:bg-white/[0.04] border-b hairline cursor-pointer"
              data-testid={`client-row-${c.id}`}
              onClick={(e) => {
                if (e.target.closest("[data-row-action]")) return;
                onRowClick?.(c);
              }}
            >
              <TableCell>
                <div className="flex items-center gap-2">
                  <Users size={14} className="text-gold" />
                  <span className="text-sm">{c.name}</span>
                </div>
              </TableCell>
              <TableCell className="text-sm text-zinc-400">{c.requirement || "—"}</TableCell>
              <TableCell className="font-mono text-sm text-gold">{formatCurrency(c.budget)}</TableCell>
              <TableCell className="text-xs text-zinc-400">
                <div>{c.email}</div>
                <div>{c.phone}</div>
              </TableCell>
              <TableCell data-row-action>
                <button onClick={() => onDelete(c.id)} className="text-xs text-zinc-500 hover:text-red-400" data-testid={`delete-client-${c.id}`}>Remove</button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function AddPropertyDialog({ open, setOpen, onSaved, user }) {
  const initial = () => ({
    title: "", description: "",
    city: user?.city || "", area: user?.area || "", address: "",
    size_sqft: 1000, price: 5000000, bedrooms: 2,
    property_type: "Residential", sub_type: "Flats",
    listing_for: "sale", commission_unit: "percent", commission: 2,
    images: [],
    rera_id: "", gst_percent: "", stamp_duty_percent: "",
  });
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Switch listing_for between sale/rent and reset commission unit defaults
  const onListingForChange = (v) => {
    setForm((f) => ({
      ...f,
      listing_for: v,
      commission_unit: v === "rent" ? "months" : "percent",
      commission: v === "rent" ? 1 : 2,
    }));
  };

  // When type changes, reset sub_type to first valid subtype (or null for Plots)
  const onTypeChange = (v) => {
    const subs = SUBTYPES[v] || [];
    setForm((f) => ({ ...f, property_type: v, sub_type: subs.length ? subs[0] : null, bedrooms: v === "Residential" ? f.bedrooms : 0 }));
  };

  const onFilesPicked = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const remaining = 5 - form.images.length;
    if (remaining <= 0) {
      toast.error("You can attach up to 5 images.");
      return;
    }
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    try {
      const uploaded = [];
      for (const original of toUpload) {
        if (!["image/jpeg", "image/png", "image/webp"].includes(original.type)) {
          toast.error(`${original.name}: unsupported type`);
          continue;
        }
        // Resize > 2000px to 1600px on the longest side, JPEG q=0.85
        const f = await compressImage(original, 1600, 0.85);
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`${original.name}: still too large after compression (max 5 MB)`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        // IMPORTANT: do not set Content-Type; the browser sets the multipart boundary.
        const { data } = await api.post("/upload", fd);
        uploaded.push(data.path);
      }
      if (uploaded.length) {
        setForm((f) => ({ ...f, images: [...f.images, ...uploaded].slice(0, 5) }));
        toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      const payload = {
        ...form,
        size_sqft: Number(form.size_sqft),
        price: Number(form.price),
        bedrooms: Number(form.bedrooms),
        commission: Number(form.commission),
        commission_unit: form.commission_unit,
        listing_for: form.listing_for,
        image_url: form.images[0] || null,
        sub_type: form.sub_type || null,
        gst_percent: form.gst_percent === "" ? null : Number(form.gst_percent),
        stamp_duty_percent: form.stamp_duty_percent === "" ? null : Number(form.stamp_duty_percent),
        rera_id: form.rera_id || null,
      };
      await api.post("/properties", payload);
      toast.success("Property added");
      setForm(initial());
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const subOptions = SUBTYPES[form.property_type] || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(initial()); }}>
      <DialogTrigger asChild>
        <Button className="bg-gold text-ink-900 hover:bg-gold-light" data-testid="add-property-button">
          <Plus size={16} className="mr-1" /> Add Property
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-ink-800 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">New Property</DialogTitle>
          <DialogDescription className="sr-only">Form to add a new property to your inventory.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Title</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1 bg-ink-700 border-white/10" data-testid="new-prop-title" placeholder="3BHK Sea-facing Apartment — Bandra" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Type</Label>
            <Select value={form.property_type} onValueChange={onTypeChange}>
              <SelectTrigger className="mt-1 bg-ink-700 border-white/10" data-testid="new-prop-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Residential">Residential</SelectItem>
                <SelectItem value="Commercial">Commercial</SelectItem>
                <SelectItem value="Plots">Plots</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {subOptions.length > 0 ? (
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Sub-type</Label>
              <Select value={form.sub_type || ""} onValueChange={(v) => set("sub_type", v)}>
                <SelectTrigger className="mt-1 bg-ink-700 border-white/10" data-testid="new-prop-subtype"><SelectValue placeholder="Select sub-type" /></SelectTrigger>
                <SelectContent>
                  {subOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Sub-type</Label>
              <div className="mt-1 h-10 rounded-md border border-white/10 bg-ink-700/60 text-xs text-zinc-500 flex items-center px-3">Not applicable</div>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">City</Label>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1 bg-ink-700 border-white/10" data-testid="new-prop-city" placeholder="Mumbai" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Area / Locality</Label>
            <Input value={form.area} onChange={(e) => set("area", e.target.value)} className="mt-1 bg-ink-700 border-white/10" data-testid="new-prop-area" placeholder="Bandra West" />
          </div>

          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Full Address</Label>
            <Textarea value={form.address} onChange={(e) => set("address", e.target.value)} rows={2} className="mt-1 bg-ink-700 border-white/10" data-testid="new-prop-address" placeholder="Flat 301, Palm Grove Building, Carter Road, Bandra West, Mumbai 400050" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Size (sqft)</Label>
            <Input type="number" value={form.size_sqft} onChange={(e) => set("size_sqft", e.target.value)} className="mt-1 bg-ink-700 border-white/10 font-mono" data-testid="new-prop-size" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">{form.listing_for === "rent" ? "Monthly Rent (₹)" : "Price (₹)"}</Label>
            <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1 bg-ink-700 border-white/10 font-mono" data-testid="new-prop-price" placeholder={form.listing_for === "rent" ? "50000" : "25000000"} />
            <div className="text-[11px] text-zinc-500 mt-1" data-testid="new-prop-price-preview">
              {form.price ? `≈ ${formatCurrency(form.price)}` : "Enter amount in rupees (no commas) — preview in Lakh / Crore"}
            </div>
          </div>
          {form.property_type === "Residential" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Bedrooms</Label>
              <Input type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className="mt-1 bg-ink-700 border-white/10" data-testid="new-prop-bedrooms" />
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Listing for</Label>
            <Select value={form.listing_for} onValueChange={onListingForChange}>
              <SelectTrigger className="mt-1 bg-ink-700 border-white/10" data-testid="new-prop-listing-for">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sale">Sale</SelectItem>
                <SelectItem value="rent">Rent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.listing_for === "sale" ? (
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Commission %</Label>
              <Input
                type="number" step="0.1" value={form.commission}
                onChange={(e) => set("commission", e.target.value)}
                className="mt-1 bg-ink-700 border-white/10 font-mono"
                data-testid="new-prop-commission"
              />
            </div>
          ) : (
            <div className="col-span-2 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Brokerage</Label>
                <Input
                  type="number" step="0.5" min="0" value={form.commission}
                  onChange={(e) => set("commission", e.target.value)}
                  className="mt-1 bg-ink-700 border-white/10 font-mono"
                  data-testid="new-prop-commission"
                  placeholder="1"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Unit</Label>
                <Select value={form.commission_unit} onValueChange={(v) => set("commission_unit", v)}>
                  <SelectTrigger className="mt-1 bg-ink-700 border-white/10" data-testid="new-prop-commission-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">Days of rent</SelectItem>
                    <SelectItem value="months">Months of rent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="mt-1 bg-ink-700 border-white/10" data-testid="new-prop-description" placeholder="Well-ventilated unit with premium fittings, covered parking, 24x7 security..." />
          </div>

          {/* Compliance fields */}
          <div className="col-span-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 mt-1">Compliance (optional)</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">RERA Reg. ID</Label>
            <Input value={form.rera_id} onChange={(e) => set("rera_id", e.target.value)} className="mt-1 bg-ink-700 border-white/10 font-mono text-xs" data-testid="new-prop-rera" placeholder="P51900000000" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">GST %</Label>
            <Input type="number" step="0.1" value={form.gst_percent} onChange={(e) => set("gst_percent", e.target.value)} className="mt-1 bg-ink-700 border-white/10 font-mono" data-testid="new-prop-gst" placeholder="5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Stamp Duty %</Label>
            <Input type="number" step="0.1" value={form.stamp_duty_percent} onChange={(e) => set("stamp_duty_percent", e.target.value)} className="mt-1 bg-ink-700 border-white/10 font-mono" data-testid="new-prop-stamp-duty" placeholder="6" />
          </div>

          <div className="col-span-2">
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Images ({form.images.length}/5)</Label>
              <label className="text-xs text-gold hover:text-gold-light cursor-pointer" data-testid="upload-image-label">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={(e) => { onFilesPicked(e.target.files); e.target.value = ""; }}
                  data-testid="property-image-input"
                  disabled={uploading || form.images.length >= 5}
                />
                {uploading ? "Uploading…" : (form.images.length >= 5 ? "Max reached" : "+ Upload images")}
              </label>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {form.images.map((path, i) => (
                <div key={i} className="relative aspect-square rounded border border-white/10 bg-ink-700 bg-cover bg-center group"
                  style={{ backgroundImage: `url(${toImageSrc(path)})` }} data-testid={`uploaded-image-${i}`}>
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 bg-black/70 hover:bg-red-500 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-image-${i}`}
                    aria-label={`Remove image ${i + 1}`}
                  >
                    <X size={10} weight="bold" />
                  </button>
                  {i === 0 && (
                    <span className="absolute bottom-1 left-1 text-[9px] uppercase tracking-widest bg-gold/90 text-ink-900 px-1.5 py-0.5 rounded">Cover</span>
                  )}
                </div>
              ))}
              {form.images.length < 5 && (
                <label
                  className={`aspect-square rounded border border-dashed hairline flex items-center justify-center text-zinc-500 ${
                    uploading ? "opacity-50" : "hover:border-gold hover:text-gold cursor-pointer"
                  }`}
                  data-testid="upload-image-tile"
                >
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={(e) => { onFilesPicked(e.target.files); e.target.value = ""; }}
                    disabled={uploading}
                  />
                  <div className="flex flex-col items-center gap-1">
                    <ImageIcon size={18} />
                    <span className="text-[10px] uppercase tracking-wider">{uploading ? "…" : "Add"}</span>
                  </div>
                </label>
              )}
            </div>
            <div className="text-[11px] text-zinc-500 mt-2">JPG, PNG, or WebP · max 5 MB each · first image is used as cover.</div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={submit} disabled={uploading} className="bg-gold text-ink-900 hover:bg-gold-light" data-testid="save-property-button">Save property</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddClientDialog({ open, setOpen, onSaved }) {
  const EMPTY = {
    name: "", email: "", phone: "", requirement: "", budget: 0,
    preferred_city: "", preferred_area: "",
    listing_for: "any", property_type: "any", bedrooms: 0,
  };
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    try {
      await api.post("/clients", {
        ...form,
        budget: Number(form.budget) || 0,
        bedrooms: Number(form.bedrooms) || 0,
      });
      toast.success("Client added");
      setForm(EMPTY);
      setOpen(false);
      onSaved();
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-white/10 bg-transparent text-zinc-300 hover:border-gold hover:text-gold" data-testid="add-client-button">
          <Plus size={16} className="mr-1" /> Add Client
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-ink-800 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">New Client</DialogTitle><DialogDescription className="sr-only">Form to add a new client.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Client Name</Label>
            <Input placeholder="e.g. Arjun Bhatt" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 bg-ink-700 border-white/10" data-testid="new-client-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Email</Label>
              <Input placeholder="email@domain.com" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 bg-ink-700 border-white/10" data-testid="new-client-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Phone</Label>
              <Input placeholder="+91 9XXXXXXXXX" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1 bg-ink-700 border-white/10" data-testid="new-client-phone" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Requirement</Label>
            <Textarea placeholder="e.g. 3BHK near metro, under ₹2.5 Cr" value={form.requirement} onChange={(e) => set("requirement", e.target.value)} rows={2} className="mt-1 bg-ink-700 border-white/10" data-testid="new-client-requirement" />
          </div>

          {/* Smart-match preferences — used by /api/match to surface matching inventory */}
          <div className="pt-3 border-t hairline">
            <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-3 flex items-center gap-2">
              <Target size={12} weight="duotone" /> Smart-match preferences
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Preferred city</Label>
                <Input placeholder="Mumbai" value={form.preferred_city} onChange={(e) => set("preferred_city", e.target.value)} className="mt-1 bg-ink-700 border-white/10" data-testid="new-client-preferred-city" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Preferred area</Label>
                <Input placeholder="Bandra West" value={form.preferred_area} onChange={(e) => set("preferred_area", e.target.value)} className="mt-1 bg-ink-700 border-white/10" data-testid="new-client-preferred-area" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Looking to</Label>
                <Select value={form.listing_for} onValueChange={(v) => set("listing_for", v)}>
                  <SelectTrigger className="mt-1 bg-ink-700 border-white/10" data-testid="new-client-listing-for"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="sale">Buy / Sale</SelectItem>
                    <SelectItem value="rent">Rent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Property type</Label>
                <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                  <SelectTrigger className="mt-1 bg-ink-700 border-white/10" data-testid="new-client-property-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="Residential">Residential</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                    <SelectItem value="Plots">Plots</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Bedrooms (BHK)</Label>
                <Input type="number" min={0} max={20} placeholder="0 = any" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className="mt-1 bg-ink-700 border-white/10 font-mono" data-testid="new-client-bedrooms" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Budget</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gold text-sm font-mono">₹</span>
                  <Input
                    type="number"
                    value={form.budget}
                    onChange={(e) => set("budget", e.target.value)}
                    placeholder="25000000"
                    className="bg-ink-700 border-white/10 font-mono pl-7"
                    data-testid="new-client-budget"
                  />
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">{form.budget ? `≈ ${formatCurrency(form.budget)}` : "Enter in Indian rupees (no commas)"}</div>
              </div>
            </div>
            <div className="text-[11px] text-zinc-500 mt-3">Smart-match uses these fields to surface inventory across your network that fits this client.</div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="bg-gold text-ink-900 hover:bg-gold-light" data-testid="save-client-button">Save client</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PropertyDetailDialog({ prop, onClose, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [zoomIdx, setZoomIdx] = useState(null); // index of image being zoomed
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shareDmOpen, setShareDmOpen] = useState(false);
  const [matches, setMatches] = useState(null); // { mode, results, loose_count, strict_count }
  const [matchMode, setMatchMode] = useState("loose");

  useEffect(() => {
    if (prop) {
      setForm({
        title: prop.title || "",
        description: prop.description || "",
        city: prop.city || "",
        area: prop.area || "",
        address: prop.address || "",
        size_sqft: prop.size_sqft || 0,
        price: prop.price || 0,
        bedrooms: prop.bedrooms || 0,
        property_type: prop.property_type || "Residential",
        sub_type: prop.sub_type || (SUBTYPES[prop.property_type || "Residential"][0] || null),
        listing_for: prop.listing_for || "sale",
        commission: prop.commission ?? 0,
        commission_unit: prop.commission_unit || "percent",
        rera_id: prop.rera_id || "",
        gst_percent: prop.gst_percent ?? "",
        stamp_duty_percent: prop.stamp_duty_percent ?? "",
        images: prop.images?.length ? [...prop.images] : (prop.image_url ? [prop.image_url] : []),
        status: prop.status || "available",
      });
      setEditing(false);
      setZoomIdx(null);
      setMatches(null);
      setMatchMode("loose");
      // Load smart-match clients
      api.get(`/match/property/${prop.id}`, { params: { mode: "loose" } })
        .then(({ data }) => setMatches(data))
        .catch(() => setMatches({ results: [], loose_count: 0, strict_count: 0 }));
    }
  }, [prop?.id]); // eslint-disable-line

  if (!prop || !form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const subOptions = SUBTYPES[form.property_type] || [];

  const formattedPrice = formatCurrency(prop.status === "sold" ? (prop.sold_price || prop.price) : prop.price);

  const commissionLabel = () => {
    const c = prop.commission;
    if (c == null || c === 0) return "—";
    const unit = prop.commission_unit || "percent";
    if (unit === "percent") return `${c}% commission`;
    if (unit === "days") return `${c} day${c === 1 ? "" : "s"} of rent`;
    if (unit === "months") return `${c} month${c === 1 ? "" : "s"} of rent`;
    return `${c}`;
  };

  const handleAddImages = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const remaining = 5 - form.images.length;
    if (remaining <= 0) return toast.error("Max 5 images");
    setUploading(true);
    try {
      const uploaded = [];
      for (const original of files.slice(0, remaining)) {
        if (!["image/jpeg", "image/png", "image/webp"].includes(original.type)) continue;
        const f = await compressImage(original, 1600, 0.85);
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd);
        uploaded.push(data.path);
      }
      if (uploaded.length) {
        setForm((s) => ({ ...s, images: [...s.images, ...uploaded].slice(0, 5) }));
        toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} added`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImg = (i) => setForm((s) => ({ ...s, images: s.images.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        city: form.city,
        area: form.area,
        address: form.address,
        size_sqft: Number(form.size_sqft),
        price: Number(form.price),
        bedrooms: Number(form.bedrooms || 0),
        property_type: form.property_type,
        sub_type: form.sub_type || null,
        listing_for: form.listing_for,
        commission: Number(form.commission || 0),
        commission_unit: form.commission_unit,
        rera_id: form.rera_id || null,
        gst_percent: form.gst_percent === "" || form.gst_percent == null ? null : Number(form.gst_percent),
        stamp_duty_percent: form.stamp_duty_percent === "" || form.stamp_duty_percent == null ? null : Number(form.stamp_duty_percent),
        images: form.images,
        image_url: form.images[0] || null,
        status: form.status,
        // refresh location from area+city
        location: [form.area, form.city].filter(Boolean).join(", "),
      };
      await api.patch(`/properties/${prop.id}`, payload);
      toast.success("Property updated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const markSold = async () => {
    try {
      await api.patch(`/properties/${prop.id}`, { status: "sold" });
      toast.success("Marked as sold");
      onSaved?.();
    } catch { toast.error("Failed"); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this property?")) return;
    try {
      await api.delete(`/properties/${prop.id}`);
      toast.success("Deleted");
      onSaved?.();
    } catch { toast.error("Failed"); }
  };

  // ─── Share helpers ─────────────────────────────────────────────────────
  const publicUrl = `${window.location.origin}/p/${prop.id}`;
  const shareText = `${prop.title} — ${formatCurrency(prop.price)} · ${prop.location || ""}\n${publicUrl}`;
  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank", "noopener");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied");
    } catch { toast.error("Couldn't copy"); }
  };

  // Switch smart-match mode between strict/loose
  const switchMatchMode = async (mode) => {
    setMatchMode(mode);
    try {
      const { data } = await api.get(`/match/property/${prop.id}`, { params: { mode } });
      setMatches(data);
    } catch { /* silent */ }
  };

  return (
    <Dialog open={!!prop} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-ink-800 border-white/10 max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="property-detail-modal">
        <DialogHeader className="sr-only">
          <DialogTitle>{prop.title}</DialogTitle>
          <DialogDescription>{prop.description || "Property detail view"}</DialogDescription>
        </DialogHeader>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap pb-5 border-b hairline">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.3em]">
              <span className="text-gold">{prop.property_type}{prop.sub_type ? ` · ${prop.sub_type}` : ""}</span>
              <Badge className={prop.listing_for === "rent" ? "bg-sky-500/10 text-sky-300 border border-sky-500/30 hover:bg-sky-500/10" : "bg-gold/10 text-gold border border-gold/30 hover:bg-gold/10"}>
                For {prop.listing_for === "rent" ? "Rent" : "Sale"}
              </Badge>
              <Badge className={prop.status === "sold" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/10" : "bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/5"}>
                {prop.status === "sold" ? "Sold" : "Available"}
              </Badge>
            </div>
            <div className="font-display text-3xl md:text-4xl tracking-tight mt-1" data-testid="detail-title">{prop.title}</div>
            <div className="flex items-start gap-1.5 text-sm text-zinc-400 mt-2">
              <MapPin size={14} className="text-gold mt-0.5 flex-shrink-0" />
              <span>{prop.address || `${prop.area || ""}${prop.city ? `, ${prop.city}` : ""}`}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-3xl text-gold" data-testid="detail-price">
              {formattedPrice}{prop.listing_for === "rent" ? <span className="text-sm text-zinc-500 ml-1">/mo</span> : null}
            </div>
            {prop.commission > 0 && (
              <div className="text-xs text-zinc-500 mt-1">{commissionLabel()}</div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-6 pt-5">
          {/* Left: details / edit form */}
          <div className="md:col-span-3 space-y-5">
            {!editing ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <Meta icon={Ruler} label="Size" value={`${prop.size_sqft} sqft`} />
                  {prop.bedrooms > 0 && <Meta icon={Bed} label="Bedrooms" value={`${prop.bedrooms} BHK`} />}
                  <Meta icon={HouseIcon} label="Type" value={prop.sub_type || prop.property_type} />
                  <Meta
                    icon={CurrencyInr}
                    label={prop.status === "sold" ? "Sold on" : "Listed"}
                    value={prop.status === "sold" ? (prop.sold_date || "—") : (prop.listing_date || "—")}
                  />
                </div>

                {prop.description && (
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-2">Description</div>
                    <p className="text-zinc-300 leading-relaxed" data-testid="detail-description">{prop.description}</p>
                  </div>
                )}

                {(prop.rera_id || prop.gst_percent != null || prop.stamp_duty_percent != null) && (
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">Compliance</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="detail-compliance">
                      {prop.rera_id && <Meta icon={HouseIcon} label="RERA" value={prop.rera_id} />}
                      {prop.gst_percent != null && <Meta icon={CurrencyInr} label="GST" value={`${prop.gst_percent}%`} />}
                      {prop.stamp_duty_percent != null && <Meta icon={CurrencyInr} label="Stamp Duty" value={`${prop.stamp_duty_percent}%`} />}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-3" data-testid="detail-edit-form">
                <FieldRow label="Title">
                  <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-ink-700 border-white/10" data-testid="edit-prop-title" />
                </FieldRow>
                <FieldRow label="Description">
                  <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-ink-700 border-white/10" />
                </FieldRow>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="City">
                    <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-ink-700 border-white/10" />
                  </FieldRow>
                  <FieldRow label="Area">
                    <Input value={form.area} onChange={(e) => set("area", e.target.value)} className="bg-ink-700 border-white/10" />
                  </FieldRow>
                </div>
                <FieldRow label="Address">
                  <Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-ink-700 border-white/10" />
                </FieldRow>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Type">
                    <Select value={form.property_type} onValueChange={(v) => {
                      const subs = SUBTYPES[v] || [];
                      setForm((f) => ({ ...f, property_type: v, sub_type: subs.length ? subs[0] : null }));
                    }}>
                      <SelectTrigger className="bg-ink-700 border-white/10" data-testid="edit-prop-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Residential">Residential</SelectItem>
                        <SelectItem value="Commercial">Commercial</SelectItem>
                        <SelectItem value="Plots">Plots</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldRow>
                  <FieldRow label="Sub-type">
                    {subOptions.length > 0 ? (
                      <Select value={form.sub_type || ""} onValueChange={(v) => set("sub_type", v)}>
                        <SelectTrigger className="bg-ink-700 border-white/10"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {subOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="h-10 rounded-md border border-white/10 bg-ink-700/60 text-xs text-zinc-500 flex items-center px-3">Not applicable</div>
                    )}
                  </FieldRow>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <FieldRow label="Size (sqft)">
                    <Input type="number" value={form.size_sqft} onChange={(e) => set("size_sqft", e.target.value)} className="bg-ink-700 border-white/10 font-mono" />
                  </FieldRow>
                  <FieldRow label={form.listing_for === "rent" ? "Monthly Rent (₹)" : "Price (₹)"}>
                    <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="bg-ink-700 border-white/10 font-mono" data-testid="edit-prop-price" />
                    <div className="text-[11px] text-zinc-500 mt-1" data-testid="edit-prop-price-preview">
                      {form.price ? `≈ ${formatCurrency(form.price)}` : ""}
                    </div>
                  </FieldRow>
                  {form.property_type === "Residential" && (
                    <FieldRow label="Bedrooms">
                      <Input type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className="bg-ink-700 border-white/10" />
                    </FieldRow>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Listing for">
                    <Select value={form.listing_for} onValueChange={(v) => set("listing_for", v) || setForm((f) => ({ ...f, listing_for: v, commission_unit: v === "rent" ? "months" : "percent" }))}>
                      <SelectTrigger className="bg-ink-700 border-white/10" data-testid="edit-prop-listing-for"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sale">Sale</SelectItem>
                        <SelectItem value="rent">Rent</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldRow>
                  <FieldRow label="Status">
                    <Select value={form.status} onValueChange={(v) => set("status", v)}>
                      <SelectTrigger className="bg-ink-700 border-white/10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="sold">Sold</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldRow>
                </div>
                {form.listing_for === "sale" ? (
                  <FieldRow label="Commission %">
                    <Input type="number" step="0.1" value={form.commission} onChange={(e) => set("commission", e.target.value)} className="bg-ink-700 border-white/10 font-mono" />
                  </FieldRow>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <FieldRow label="Brokerage">
                      <Input type="number" step="0.5" value={form.commission} onChange={(e) => set("commission", e.target.value)} className="bg-ink-700 border-white/10 font-mono" />
                    </FieldRow>
                    <FieldRow label="Unit">
                      <Select value={form.commission_unit} onValueChange={(v) => set("commission_unit", v)}>
                        <SelectTrigger className="bg-ink-700 border-white/10"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="days">Days of rent</SelectItem>
                          <SelectItem value="months">Months of rent</SelectItem>
                        </SelectContent>
                      </Select>
                    </FieldRow>
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 pt-2">Compliance</div>
                <div className="grid grid-cols-3 gap-3">
                  <FieldRow label="RERA"><Input value={form.rera_id} onChange={(e) => set("rera_id", e.target.value)} className="bg-ink-700 border-white/10 font-mono text-xs" /></FieldRow>
                  <FieldRow label="GST %"><Input type="number" step="0.1" value={form.gst_percent} onChange={(e) => set("gst_percent", e.target.value)} className="bg-ink-700 border-white/10 font-mono" /></FieldRow>
                  <FieldRow label="Stamp Duty %"><Input type="number" step="0.1" value={form.stamp_duty_percent} onChange={(e) => set("stamp_duty_percent", e.target.value)} className="bg-ink-700 border-white/10 font-mono" /></FieldRow>
                </div>
              </div>
            )}
          </div>

          {/* Right: image gallery */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-[0.25em] text-zinc-400">Photos ({form.images.length})</div>
              {editing && form.images.length < 5 && (
                <label className="text-xs text-gold hover:text-gold-light cursor-pointer">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={(e) => { handleAddImages(e.target.files); e.target.value = ""; }}
                    data-testid="edit-prop-add-images"
                  />
                  {uploading ? "Uploading…" : "+ Add"}
                </label>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {form.images.map((src, i) => (
                <div
                  key={i}
                  className="aspect-square rounded border border-white/10 bg-ink-700 bg-cover bg-center relative group cursor-zoom-in"
                  style={{ backgroundImage: `url(${toImageSrc(src)})` }}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => !editing && setZoomIdx(i)}
                >
                  {editing && (
                    <button
                      type="button"
                      onClick={() => removeImg(i)}
                      className="absolute top-1 right-1 bg-black/70 hover:bg-red-500 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`edit-remove-image-${i}`}
                    >
                      <X size={10} weight="bold" />
                    </button>
                  )}
                  {i === 0 && (
                    <span className="absolute bottom-1 left-1 text-[9px] uppercase tracking-widest bg-gold/90 text-ink-900 px-1.5 py-0.5 rounded">Cover</span>
                  )}
                </div>
              ))}
              {form.images.length === 0 && (
                <div className="col-span-2 aspect-video flex items-center justify-center bg-ink-700 rounded text-zinc-600">
                  <ImageIcon size={24} />
                </div>
              )}
            </div>
            <div className="text-[11px] text-zinc-500 mt-2">{editing ? "Click a thumbnail to remove. First image is the cover." : "Click any photo to enlarge."}</div>
          </div>
        </div>

        {/* Smart match — clients that fit this property */}
        {!editing && matches && (matches.loose_count > 0 || matches.strict_count > 0) && (
          <div className="mt-5 card-surface rounded-xl p-4" data-testid="smart-match-panel">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Target size={16} className="text-gold" weight="duotone" />
                <div className="text-sm">
                  <span className="text-gold font-medium" data-testid="smart-match-count">{matches.results?.length || 0}</span>
                  <span className="text-zinc-500"> of your clients match this property</span>
                </div>
              </div>
              <div className="flex items-center gap-1 text-[11px]">
                <button
                  type="button"
                  onClick={() => switchMatchMode("loose")}
                  data-testid="match-mode-loose"
                  className={`px-2 py-1 rounded ${matchMode === "loose" ? "bg-gold/20 text-gold" : "text-zinc-500 hover:text-zinc-300"}`}
                >Loose ({matches.loose_count})</button>
                <button
                  type="button"
                  onClick={() => switchMatchMode("strict")}
                  data-testid="match-mode-strict"
                  className={`px-2 py-1 rounded ${matchMode === "strict" ? "bg-gold/20 text-gold" : "text-zinc-500 hover:text-zinc-300"}`}
                >Strict ({matches.strict_count})</button>
              </div>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {(matches.results || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between p-2 rounded-md hover:bg-white/[0.03]" data-testid={`smart-match-client-${m.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm truncate">{m.name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {(m.reasons || []).join(" · ")}
                    </div>
                  </div>
                  <div className="font-mono text-xs text-gold whitespace-nowrap">{formatCurrency(m.budget)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action bar */}
        <div className="flex items-center justify-between pt-5 mt-3 border-t hairline flex-wrap gap-2">
          {!editing ? (
            <>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" className="border-white/10 bg-transparent hover:border-gold hover:text-gold" onClick={() => setEditing(true)} data-testid="enter-edit-mode">
                  Edit details
                </Button>
                {prop.status !== "sold" && (
                  <Button variant="outline" className="border-white/10 bg-transparent hover:border-emerald-400 hover:text-emerald-400" onClick={markSold} data-testid="modal-mark-sold">
                    <CheckCircle size={14} className="mr-1" /> Mark sold
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="border-white/10 bg-transparent hover:border-gold hover:text-gold gap-2" data-testid="share-property-trigger">
                      <ShareNetwork size={14} /> Share
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-ink-800 border-white/10">
                    <DropdownMenuItem onClick={shareWhatsApp} data-testid="share-whatsapp" className="gap-2">
                      <WhatsappLogo size={14} className="text-emerald-400" /> Share to WhatsApp
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={copyLink} data-testid="share-copy-link" className="gap-2">
                      <Copy size={14} /> Copy public link
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setShareDmOpen(true)} data-testid="share-dm" className="gap-2">
                      <PaperPlaneTilt size={14} className="text-gold" /> Send to a friend (DM)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <Button variant="ghost" className="text-red-400 hover:bg-red-500/10 hover:text-red-300" onClick={remove} data-testid="modal-delete">
                Delete
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => setEditing(false)} data-testid="cancel-edit">Cancel</Button>
              <Button className="bg-gold text-ink-900 hover:bg-gold-light" onClick={save} disabled={saving} data-testid="save-edit">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </>
          )}
        </div>

        <ShareToDmDialog open={shareDmOpen} onOpenChange={setShareDmOpen} property={prop} />

        {/* Zoom overlay */}
        {zoomIdx != null && form.images[zoomIdx] && (
          <div
            className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
            onClick={() => setZoomIdx(null)}
            data-testid="image-zoom-overlay"
          >
            <img
              src={toImageSrc(form.images[zoomIdx])}
              alt="Property"
              className="max-h-full max-w-full object-contain rounded"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ClientDetailDialog({ client, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name || "",
        email: client.email || "",
        phone: client.phone || "",
        requirement: client.requirement || "",
        budget: client.budget ?? 0,
        preferred_city: client.preferred_city || "",
        preferred_area: client.preferred_area || "",
        listing_for: client.listing_for || "any",
        property_type: client.property_type || "any",
        bedrooms: client.bedrooms ?? 0,
      });
      setEditing(false);
    }
  }, [client?.id]); // eslint-disable-line

  if (!client || !form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/clients/${client.id}`, {
        ...form,
        budget: Number(form.budget || 0),
        bedrooms: Number(form.bedrooms || 0),
      });
      toast.success("Client updated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove this client?")) return;
    await api.delete(`/clients/${client.id}`);
    toast.success("Client removed");
    onSaved?.();
  };

  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-ink-800 border-white/10 max-w-lg" data-testid="client-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{client.name}</DialogTitle>
          <DialogDescription className="sr-only">Client detail view</DialogDescription>
        </DialogHeader>

        {!editing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Meta icon={Envelope} label="Email" value={client.email || "—"} />
              <Meta icon={Phone} label="Phone" value={client.phone || "—"} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-1">Requirement</div>
              <p className="text-zinc-300">{client.requirement || "—"}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-1">Budget</div>
              <div className="font-mono text-2xl text-gold">{formatCurrency(client.budget)}</div>
            </div>
            {/* Smart-match preferences (read-only) */}
            {(client.preferred_city || client.preferred_area || (client.listing_for && client.listing_for !== "any") || (client.property_type && client.property_type !== "any") || client.bedrooms > 0) && (
              <div className="pt-3 border-t hairline">
                <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-2 flex items-center gap-2"><Target size={12} weight="duotone" /> Smart-match preferences</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {client.preferred_city && <Meta icon={MapPin} label="Preferred city" value={client.preferred_city} />}
                  {client.preferred_area && <Meta icon={MapPin} label="Preferred area" value={client.preferred_area} />}
                  {client.listing_for && client.listing_for !== "any" && <Meta icon={HouseIcon} label="Looking to" value={client.listing_for === "sale" ? "Buy / Sale" : "Rent"} />}
                  {client.property_type && client.property_type !== "any" && <Meta icon={HouseIcon} label="Property type" value={client.property_type} />}
                  {client.bedrooms > 0 && <Meta icon={Bed} label="Bedrooms" value={`${client.bedrooms} BHK`} />}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1" data-testid="client-edit-form">
            <FieldRow label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-ink-700 border-white/10" /></FieldRow>
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-ink-700 border-white/10" /></FieldRow>
              <FieldRow label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-ink-700 border-white/10" /></FieldRow>
            </div>
            <FieldRow label="Requirement"><Textarea rows={2} value={form.requirement} onChange={(e) => set("requirement", e.target.value)} className="bg-ink-700 border-white/10" /></FieldRow>
            <FieldRow label="Budget">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gold text-sm font-mono">₹</span>
                <Input type="number" value={form.budget} onChange={(e) => set("budget", e.target.value)} className="bg-ink-700 border-white/10 font-mono pl-7" data-testid="edit-client-budget" />
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">{form.budget ? `≈ ${formatCurrency(form.budget)}` : ""}</div>
            </FieldRow>
            {/* Smart-match preferences (editable) */}
            <div className="pt-3 border-t hairline">
              <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-3 flex items-center gap-2"><Target size={12} weight="duotone" /> Smart-match preferences</div>
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="Preferred city"><Input value={form.preferred_city} onChange={(e) => set("preferred_city", e.target.value)} className="bg-ink-700 border-white/10" data-testid="edit-client-preferred-city" /></FieldRow>
                <FieldRow label="Preferred area"><Input value={form.preferred_area} onChange={(e) => set("preferred_area", e.target.value)} className="bg-ink-700 border-white/10" data-testid="edit-client-preferred-area" /></FieldRow>
                <FieldRow label="Looking to">
                  <Select value={form.listing_for} onValueChange={(v) => set("listing_for", v)}>
                    <SelectTrigger className="bg-ink-700 border-white/10" data-testid="edit-client-listing-for"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="sale">Buy / Sale</SelectItem>
                      <SelectItem value="rent">Rent</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Property type">
                  <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                    <SelectTrigger className="bg-ink-700 border-white/10" data-testid="edit-client-property-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="Residential">Residential</SelectItem>
                      <SelectItem value="Commercial">Commercial</SelectItem>
                      <SelectItem value="Plots">Plots</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Bedrooms (BHK)">
                  <Input type="number" min={0} max={20} value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className="bg-ink-700 border-white/10 font-mono" data-testid="edit-client-bedrooms" />
                </FieldRow>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between sm:justify-between gap-2 pt-3 border-t hairline">
          {!editing ? (
            <>
              <Button variant="outline" className="border-white/10 bg-transparent hover:border-gold hover:text-gold" onClick={() => setEditing(true)} data-testid="enter-client-edit">Edit</Button>
              <Button variant="ghost" className="text-red-400 hover:bg-red-500/10 hover:text-red-300" onClick={remove} data-testid="client-modal-delete">Remove</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => setEditing(false)} data-testid="cancel-client-edit">Cancel</Button>
              <Button className="bg-gold text-ink-900 hover:bg-gold-light" disabled={saving} onClick={save} data-testid="save-client-edit">{saving ? "Saving…" : "Save changes"}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon size={16} className="text-gold" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <div className="text-sm font-mono mt-0.5">{value}</div>
      </div>
    </div>
  );
}


// Pick a friend agent and send the property as a DM attachment.
function ShareToDmDialog({ open, onOpenChange, property }) {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingTo, setSendingTo] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/friends")
      .then(({ data }) => setFriends(data))
      .catch(() => toast.error("Couldn't load friends"))
      .finally(() => setLoading(false));
  }, [open]);

  const send = async (friend) => {
    setSendingTo(friend.id);
    try {
      await api.post(`/messages/${friend.id}`, {
        content: `Sharing this listing: ${property.title}`,
        attachment_type: "property",
        attachment_id: property.id,
      });
      toast.success(`Sent to ${friend.name}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSendingTo(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-ink-800 border-white/10" data-testid="share-dm-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Send to a friend</DialogTitle>
          <DialogDescription className="text-zinc-500">They&apos;ll see this listing as a card in your chat.</DialogDescription>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto space-y-1">
          {loading && <div className="p-4 text-center text-sm text-zinc-500">Loading friends…</div>}
          {!loading && friends.length === 0 && (
            <div className="p-6 text-center text-sm text-zinc-500">
              You&apos;re not connected with anyone yet. Visit Network to add friends.
            </div>
          )}
          {friends.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => send(f)}
              disabled={sendingTo === f.id}
              data-testid={`share-dm-friend-${f.id}`}
              className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-white/[0.05] transition-colors disabled:opacity-50"
            >
              <img src={toImageSrc(f.avatar_url) || ""} alt="" className="h-9 w-9 rounded-full object-cover bg-ink-700" />
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm truncate">{f.name}</div>
                <div className="text-[11px] text-zinc-500 truncate">{[f.specialty, f.area, f.city].filter(Boolean).join(" · ")}</div>
              </div>
              {sendingTo === f.id ? <span className="text-xs text-gold">Sending…</span> : <PaperPlaneTilt size={14} className="text-gold" />}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
